import { tv, type VariantProps } from "tailwind-variants"
import {
  type TextColorVariantProps,
  textColorVariants,
} from "../../styles/utils/color"
import { classNames } from "../../utils/classNames"
import { SkeletonLine, type SkeletonProps } from "../Skeleton"
import { Slot, type SlotProps } from "../Slot"

export type TextHeadingProps = SlotProps<"span"> &
  VariantProps<typeof headingVariants> &
  TextColorVariantProps &
  Required<Pick<VariantProps<typeof headingVariants>, "size">>

const headingVariants = tv({
  base: "font-medium leading-tight",
  variants: {
    size: {
      xs: "text-heading-xs",
      sm: "text-heading-sm",
      md: "text-heading-md",
      lg: "text-heading-lg",
    },
  },
})

/**
 * Use heading for lower emphasis heading.
 * The heading style is Semibold and provides a lower emphasis option to Display.
 * Can be use in pages with multiple headings and sections.
 */
function TextHeading({
  asChild,
  className,
  color,
  size,
  ...rest
}: TextHeadingProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      className={classNames(
        headingVariants({ size }),
        textColorVariants({ color }),
        className,
      )}
      {...rest}
    />
  )
}

const textHeadingSkeletonVariants = tv({
  variants: {
    size: {
      xs: "h-[18px]",
      sm: "h-5",
      md: "h-6",
      lg: "h-8",
    },
  },
})

const TextHeadingSkeleton = ({
  className,
  size,
  ...rest
}: SkeletonProps & Pick<TextHeadingProps, "size" | "className">) => {
  return (
    <SkeletonLine
      className={classNames(textHeadingSkeletonVariants({ size }), className)}
      {...rest}
    />
  )
}

export { TextHeading, TextHeadingSkeleton }
